import { useCallback, useRef } from "react";
import { pricingUtils } from "@flights/web-api-utils-universal";
import type { UIPrice, UIUnifiedPriceBreakdownAttachedAncillary } from "@flights/types";

import useTrackerContext from "../useTrackerContext";
import useGlobalContext from "../useGlobalContext";
import useRouteName from "../useRouteName";
import { type RouteName } from "@flights/constants";

const ACTION_NAME = "flights__price_rendered";
const ACTION_VERSION = "5.0.0";

export type RouteNameWithPaymentPriceChange = RouteName | "checkout-payment-price-change";

export type PriceRenderedUiComponent = "BREAKDOWN" | "TAG";

export type PriceRenderedProductPrice = {
  productType: string;
  productPrice: UIPrice;
};

type PriceRenderedPrice = {
  currency_code?: string;
  price?: string;
};

type PriceRenderedBreakdownItem = {
  item_type?: string;
  item_price?: PriceRenderedPrice;
};

type PriceRenderedAttachedAncillary = {
  ancillary_name?: string;
  ancillary_quantity?: number;
};

type PriceRenderedContent = {
  funnel_page: RouteNameWithPaymentPriceChange;
  product_id?: string;
  price_breakdown_items?: PriceRenderedBreakdownItem[];
  request_id: string;
  total_price: PriceRenderedPrice;
  ui_component: PriceRenderedUiComponent;
  attached_ancillaries?: PriceRenderedAttachedAncillary[];
};

type Content = PriceRenderedContent;

type TrackingData = {
  requestId: string;
};

const buildEventSignature = (
  uiComponent: PriceRenderedUiComponent,
  productId: string,
  ancillaries: UIUnifiedPriceBreakdownAttachedAncillary[],
  totalPrice: UIPrice
) => {
  const ancillariesSignature = ancillaries
    .map(({ id, quantity }) => `${id}_${quantity}`)
    .sort()
    .join("-");

  return [
    uiComponent,
    productId,
    ancillariesSignature,
    totalPrice.currencyCode,
    totalPrice.units,
    totalPrice.nanos
  ].join("-");
};

export const usePriceConsistencyMonitoringEvents = () => {
  const { trackExternal } = useTrackerContext();
  const routName = useRouteName();
  const { requestId } = useGlobalContext();
  const lastTrackedRef = useRef<string>("");

  return useCallback(
    (
      uiComponent: PriceRenderedUiComponent,
      productId: string,
      productPrices: PriceRenderedProductPrice[],
      totalPrice: UIPrice,
      ancillaries: UIUnifiedPriceBreakdownAttachedAncillary[],
      isPaymentPriceChange?: boolean
    ) => {
      // Prevent duplicate tracking: create a unique signature for this event
      const eventSignature = buildEventSignature(uiComponent, productId, ancillaries, totalPrice);
      if (lastTrackedRef.current === eventSignature) {
        return;
      }
      lastTrackedRef.current = eventSignature;

      const trackingData: TrackingData = { requestId };
      const content = getContent(
        isPaymentPriceChange ? "checkout-payment-price-change" : routName,
        trackingData,
        uiComponent,
        productId,
        productPrices,
        totalPrice,
        ancillaries
      );

      trackExternal({
        type: "c360",
        args: [
          {
            action_name: ACTION_NAME,
            action_version: ACTION_VERSION,
            content
          }
        ]
      });
    },
    [requestId, routName, trackExternal]
  );
};

const getContent = (
  routeName: RouteNameWithPaymentPriceChange,
  trackingData: TrackingData,
  uiComponent: PriceRenderedUiComponent,
  productId: string,
  productPrices: PriceRenderedProductPrice[],
  totalPrice: UIPrice,
  ancillaries: UIUnifiedPriceBreakdownAttachedAncillary[]
): Content => {
  // Map productPrices to items array, removing duplicate product_id
  const items: PriceRenderedBreakdownItem[] = productPrices.map(({ productPrice, productType }) => ({
    item_price: {
      currency_code: productPrice.currencyCode,
      price: String(pricingUtils.priceToValue(productPrice))
    },
    item_type: productType
  }));

  const total = {
    currency_code: totalPrice.currencyCode,
    price: String(pricingUtils.priceToValue(totalPrice))
  };

  const attachedAncillaries = ancillaries
    .sort((a, b) => a.id.localeCompare(b.id))
    .map(({ id, quantity }) => ({
      ancillary_name: id,
      ancillary_quantity: quantity
    }));

  return {
    funnel_page: routeName,
    product_id: productId, // Moved to top level (v3.0.0 schema)
    price_breakdown_items: items,
    request_id: trackingData.requestId,
    total_price: total,
    attached_ancillaries: attachedAncillaries,
    ui_component: uiComponent
  };
};

export default usePriceConsistencyMonitoringEvents;
